import { useState } from 'react';
import { Drawer, Box, Grid, Pagination } from 'components/ui';
import { FormattedMessage, useIntl } from 'react-intl';
import { useQuery } from '@apollo/client/react';
import { FilterProvider, useFilter, initTemplateFilterValues } from 'contexts/filter';
import { useError } from 'contexts/error';
import { usePaginate } from 'contexts/paginate';
import FilterForm from 'components/template/filter_form';
import FilterTags from 'components/filter/tags';
import FlexibleInput from 'components/flexible_input';
import CreateButton from 'components/template/button/create';
import TemplatesQuery from 'queries/template/index';
import Index from 'components/template/index';
import FilterButton from 'components/template/button/filter';
import { TitleTypography, SubtitleTypography } from 'components/styles_components/typography';

const IndexContainer = () => {
  const intl = useIntl();
  const { page, onPageChange } = usePaginate();
  const setError = useError();
  const { templatesFilter, setTemplatesFilter } = useFilter();
  const [filterVisible, setFilterVisible] = useState(false);
  const [search, setSearch] = useState(null);
  const [sortBy, setSortBy] = useState('name_asc');
  const {
    data = {
      templates: { templates: [], perPage: 10, templatesTotal: 0 },
      space: { adminRole: true },
    },
    loading,
  } = useQuery(TemplatesQuery, {
    variables: {
      ...templatesFilter,
      search,
      page,
      sortBy,
    },
    cachePolicy: 'cache-and-network',
    onError: () => {
      setError('error.template.index');
    },
  });

  return (
    <FilterProvider>
      <TitleTypography variant="h1">
        <FormattedMessage id="navbar.templates" />
      </TitleTypography>
      <SubtitleTypography variant="h2">
        <FormattedMessage
          id="template.total"
          values={{ count: data.templates.templatesTotal ?? 0 }}
        />
      </SubtitleTypography>
      {filterVisible && (
        <Drawer
          anchor="right"
          open={filterVisible}
          onClose={() => {
            setFilterVisible(false);
          }}
        >
          <FilterForm
            initialValues={templatesFilter}
            defaultValues={initTemplateFilterValues}
            onSubmit={(values) => {
              onPageChange(1);
              setTemplatesFilter(values);
            }}
            onClose={() => {
              setFilterVisible(false);
            }}
          />
        </Drawer>
      )}
      <Grid container spacing={1} alignItems="center" justifyContent="flex-end">
        <Grid size={{ xs: 12, sm: 'auto' }} sx={{ flexGrow: { sm: 1 }, minWidth: 0 }}>
          <FlexibleInput
            placeholder={intl.formatMessage({ id: 'flexible_filter.template' })}
            value={search}
            onChange={setSearch}
          />
        </Grid>
        <Grid size="auto" sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
          <FilterButton
            onClick={() => setFilterVisible(!filterVisible)}
            initTemplateFilterValues={initTemplateFilterValues}
            templatesFilter={templatesFilter}
          />
          {data.space.adminRole && <CreateButton />}
        </Grid>
      </Grid>
      <FilterTags
        filterValues={templatesFilter}
        defaultValues={initTemplateFilterValues}
        onFilterChange={setTemplatesFilter}
      />
      <Index
        loading={loading}
        templates={data.templates}
        sortBy={sortBy}
        onSortChange={setSortBy}
      />
      <Box display="flex" justifyContent="center">
        {data.templates.templatesTotalPages > 1 && (
          <Pagination
            count={data.templates.templatesTotalPages}
            page={page}
            onChange={(_e, nextPage) => {
              onPageChange(nextPage);
            }}
          />
        )}
      </Box>
    </FilterProvider>
  );
};

export default IndexContainer;
